import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import SEO from "../components/SEO";
import { getPageSeo, breadcrumbSchema, caseStudySchema } from "../lib/seo";

const AniwaGatheringCaseStudy = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="bg-[#101F27] text-white min-h-screen">
      <SEO
        {...getPageSeo("/case-studies/aniwa-gathering")}
        schema={[
          caseStudySchema("/case-studies/aniwa-gathering"),
          breadcrumbSchema([
          { name: "Home", path: "/" },
          { name: "Case Studies", path: "/case-studies" },
          { name: "Aniwa Gathering", path: "/case-studies/aniwa-gathering" },
        ]),
        ]}
      />
      {/* Page Title Bar */}
      <section className="bg-[#37464F]/30 border-b border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 items-center">
            <div className="lg:col-span-2">
              <Link
                to="/case-studies"
                className="inline-flex items-center text-[#009688] hover:underline mb-6 text-sm font-semibold"
                data-testid="back-to-case-studies-link"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Case Studies
              </Link>
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white leading-tight">
                Aniwa Gathering: A Cashless Event Triumph in the Heart of Nature
              </h1>
            </div>
            <div className="lg:col-span-3">
              <div
                className="w-full h-72 md:h-96 rounded-lg bg-cover bg-center shadow-2xl"
                style={{
                  backgroundImage:
                    "url('/images/case-studies/aniwa-gathering.jpg')",
                }}
                role="img"
                aria-label="Aniwa Gathering"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Case Study Content */}
      <section className="py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <article className="prose prose-invert max-w-none">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Overview</h2>
            <p className="text-lg text-gray-300 leading-relaxed mb-10">
              In May 2023, the organizing team behind Aniwa Gathering faced an intriguing challenge: hosting a cashless event in the serene wilderness of Big Bear, a remote park several hours' drive from Los Angeles, California. This unique setting posed connectivity obstacles as there was no reliable internet or mobile data service available, except for a satellite internet service located at the central organizing station. Seeking a seamless cashless solution under these constraints, they turned to WristCoin.
            </p>

            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Approach</h2>
            <p className="text-lg text-gray-300 leading-relaxed mb-6">
              WristCoin, renowned for its adaptability, rose to the occasion. Leveraging our credit card top-up kiosks, capable of adding funds to wristbands through integrated card readers, we crafted a solution that could withstand the rigors of a remote event. Multiple self-serve kiosks were strategically placed at the central location where WiFi connectivity was accessible via the satellite internet service.
            </p>
            <p className="text-lg text-gray-300 leading-relaxed mb-6">
              Guests at Aniwa Gathering simply entered the desired amount to top up, tapped their credit cards, and loaded their wristbands. This straightforward process ensured that attendees could seamlessly transition to a cashless experience. However, the true challenge lay in facilitating secure, offline transactions at vendor locations scattered far from the central area, devoid of WiFi or internet access.
            </p>
            <p className="text-lg text-gray-300 leading-relaxed mb-10">
              WristCoin's solution addressed this hurdle by enabling vendors to process all transactions offline. Each vendor's terminal was equipped to securely manage transactions without the need for real-time internet connectivity. Instead, all transactional data was stored locally.
            </p>

            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Outcome</h2>
            <p className="text-lg text-gray-300 leading-relaxed mb-6">
              The implementation of WristCoin's cashless solution at Aniwa Gathering 2023 proved to be a resounding success. Despite the event's remote location and limited internet access, attendees enjoyed a hassle-free cashless experience.
            </p>
            <p className="text-lg text-gray-300 leading-relaxed mb-6">
              The credit card top-up kiosks provided a user-friendly method for guests to add funds to their wristbands, ensuring they could make purchases effortlessly. Vendors, stationed in areas without internet access, efficiently processed transactions offline, guaranteeing security and convenience.
            </p>
            <p className="text-lg text-gray-300 leading-relaxed mb-10">
              At the conclusion of the event, the team effortlessly connected the vendor terminals to WiFi at the central location. This step facilitated the upload of all essential transactional data, completing the cashless cycle seamlessly.
            </p>

            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Conclusion</h2>
            <p className="text-lg text-gray-300 leading-relaxed mb-6">
              Aniwa Gathering 2023's collaboration with WristCoin exemplified the power of innovative solutions in challenging environments. WristCoin's adaptability and commitment to problem-solving enabled a cashless experience amidst the natural splendor of Big Bear, California.
            </p>
            <p className="text-lg text-gray-300 leading-relaxed mb-6">
              This case study underscores how WristCoin's cashless solution transcends geographical barriers, offering a reliable and secure means of conducting transactions even in remote locations with limited internet access. In summary, Aniwa Gathering's journey with WristCoin was a testament to ingenuity and the successful fusion of technology with nature's tranquility.
            </p>
          </article>
        </div>
      </section>
    </div>
  );
};

export default AniwaGatheringCaseStudy;
